import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StickyNote, Save, Trash2, AlertTriangle } from "lucide-react";
import { fmtMoney } from "@/lib/format";

export function NoteModal({ tx, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [note, setNote] = useState(tx?.admin_note || "");
  const [loading, setLoading] = useState(false);
  if (!tx) return null;
  const submit = async () => {
    setLoading(true);
    await onConfirm(tx, note.trim());
    setLoading(false);
  };
  const remove = async () => {
    setLoading(true);
    await onConfirm(tx, "");
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="note-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><StickyNote className="text-blue-600" size={20} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-xl text-slate-900">{t("approvals.note_title")}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {tx.reference} · {fmtMoney(Math.abs(tx.amount), tx.currency)} · {tx.beneficiary_name}
            </p>
          </div>
        </div>
        <label className="block mt-5">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("approvals.note_title")}</span>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={4} data-testid="note-text"
            placeholder={t("approvals.note_placeholder")}
            className="w-full mt-2 px-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 text-sm"
          />
          <span className="block mt-2 text-[11px] text-slate-400">{t("approvals.note_hint")}</span>
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="note-cancel">{t("approvals.cancel")}</button>
          {tx.admin_note && (
            <button onClick={remove} disabled={loading} data-testid="note-delete"
                    className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 border border-red-200 font-semibold px-4 py-2.5 rounded-full text-sm">
              <Trash2 size={16} /> {t("approvals.note_delete")}
            </button>
          )}
          <button onClick={submit} disabled={loading}
                  data-testid="note-confirm"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm">
            {loading ? "…" : <><Save size={16} /> {t("approvals.note_save")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TxAlertModal({ tx, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(tx?.receipt_alert_mode || (tx?.receipt_alert ? "on" : "inherit"));
  const [title, setTitle] = useState(tx?.receipt_alert_title || "");
  const [message, setMessage] = useState(tx?.receipt_alert_message || "");
  const [loading, setLoading] = useState(false);
  if (!tx) return null;
  const submit = async () => {
    setLoading(true);
    await onConfirm(tx, { mode, title: title.trim() || null, message: message.trim() || null });
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="tx-alert-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><AlertTriangle className="text-amber-600" size={20} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-xl text-slate-900">{t("approvals.tx_alert_title", { defaultValue: "Aviso de este comprobante" })}</h3>
            <p className="text-sm text-slate-500 mt-1">{tx.reference} · {tx.beneficiary_name}</p>
          </div>
        </div>
        <div className="mt-4">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("approvals.tx_alert_mode", { defaultValue: "Mostrar aviso" })}</span>
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="tx-alert-mode"
            className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm outline-none focus:border-amber-500">
            <option value="inherit">{t("approvals.tx_alert_inherit", { defaultValue: "Heredar (cliente / global)" })}</option>
            <option value="on">{t("approvals.tx_alert_on", { defaultValue: "Activar en este comprobante" })}</option>
            <option value="off">{t("approvals.tx_alert_off", { defaultValue: "Ocultar en este comprobante" })}</option>
          </select>
        </div>
        {mode === "on" && (
          <div className="mt-3 space-y-2">
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="tx-alert-title-input"
              placeholder={t("approvals.tx_alert_title_ph", { defaultValue: "Título (vacío = usa el global)" })}
              className="w-full px-3 py-2 rounded-xl border border-amber-300 text-sm outline-none focus:border-amber-500" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="tx-alert-message-input" rows={2}
              placeholder={t("approvals.tx_alert_msg_ph", { defaultValue: "Mensaje (vacío = usa el global)" })}
              className="w-full px-3 py-2 rounded-xl border border-amber-300 text-sm outline-none focus:border-amber-500" />
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">{t("approvals.cancel")}</button>
          <button onClick={submit} disabled={loading} data-testid="tx-alert-save"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            {loading ? "…" : <><Save size={16} /> {t("approvals.tx_alert_save", { defaultValue: "Guardar" })}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
